// stores/vm.ts
import { defineStore } from 'pinia'
import { markRaw, computed, ref } from 'vue'
import type { Component } from 'vue'
import type { AnyThing } from '@/components/ProductTypes'
import { useTagStore } from './tags' 
import { generateLdJson } from "~/utils/ldJson";
import {parseJwt, loadInitialAuthData} from "../utils/jwtToken.js"
import { stringify } from 'node:querystring'
export interface Cart { name: string; items: [] }


export interface VmState {
  env: {
    backendURL: string
    turnstile_backendURL: string
  }
  isFeedbackOpen: boolean
  isAdmin: boolean
  isSafari: boolean
  isMobile: boolean
  showModalDialog: boolean
  user: { jwtToken: string, loginExpires: string, countryCode: string}
  filterParams: { category: string | null }
  tags: string[]
  vueFlowHeight: string
  carouselHeight: string
  carousel: boolean
  carouselProdcuts: AnyThing[]
  lastSelectedFeedbackID: string | null
  selectedCart: string
  isRightPanelOpen: boolean
  rightPanelComponent: Component | null
  products: AnyThing[]
  carts: Cart[]  // added
}

export const useVmStore = defineStore('vm', () => {
  // --- State ---
  const env = {
    backendURL: import.meta.env.VITE_CLOUD_URL,
    turnstile_backendURL: import.meta.env.VITE_R2_CLOUDFLARE_URL,
  }

  const isFeedbackOpen = ref(false)
  const isAdmin = ref(false)
  const isSafari = ref(false)
  const isMobile = ref(false)
  const showModalDialog = ref(false)

  const { token: initialJwtToken, expiry: initialJwtTokenExpiry, countryCode: initialJwtCountryCode, email: initialUserEmail } = loadInitialAuthData()
  const user = ref({ email:initialUserEmail, jwtToken: initialJwtToken, loginExpires: initialJwtTokenExpiry, countryCode: initialJwtCountryCode })

  const filterParams = ref<{ category: string | null }>({ category: null })
  const tags = ref<string[]>([])
  const carouselPercentage = ref('0') // just set this to show or hide
  const lastSelectedFeedbackID = ref<string | null>(null)
  const selectedCart = ref("Shopping Cart")
  const isRightPanelOpen = ref(false)
  const rightPanelComponent = ref<Component | null>(null)
  const products = ref<AnyThing[]>([])
  const searchQuery = ref("")
  const selectedCategory = ref("")
  const pendingImages = ref(0)  /** how many async image loads are in flight */
  const loadingImages = computed(() => pendingImages.value > 0)  /** true while any images are still loading */
  const selectedProducts = ref([]) //Products user selected, NodeWatcher.vue uses vueflow.ts's useSelectedNodesSync to make this magic happen

  // --- Carts state ---
  const carts = ref<Cart[]>([
    { name: 'Bag', items: [] },
    { name: 'Save for Later', items: [] },
  ])



  // --- Getters ---
  const categories = computed(() => [
    ...new Set(products.value.map((p) => p.category)),
  ])

  const showCarousel = computed(() => { 
    if (carouselPercentage.value == '0' || carouselPercentage.value =='0.0' || carouselPercentage.value == null) { 
      return false;
    } else {
      return true;
    }
  })

  /** bump counter when a load starts */
  function imageLoadStart() {
    pendingImages.value++
  }
  /** bump down when it finishes */
  function imageLoadEnd() {
    if (pendingImages.value > 0) pendingImages.value--
  }
  const carouselHeight = computed(() => { 
     const pct = parseFloat(carouselPercentage.value)
    return `calc((100dvh - 86px) * ${pct})`;
  })

  const vueFlowHeight = computed(() => { 
     const pct = 1 - parseFloat(carouselPercentage.value)
    return `calc((100dvh - 86px) * ${pct})`;
  })

  // Format products for carousel
  const carouselProducts = computed(() => {
    return selectedProducts.value.slice(0, 11).map(product => ({
      ...product,
      image: product.image || `/fallback.jpeg`,
      description: product.description?.substring(0, 120) || "Product description",
    }));
  });

  //products list filtered by currently selected tags
  const filteredProducts = computed(() => {
    const tagStore = useTagStore()
    const selected = tagStore.selected

    if (selected.length === 0) return []

    return products.value.filter(product =>
      Array.isArray(product.tags) &&
      selected.every(tag => product.tags.includes(tag))
    )
  })

   // --- JSON-LD injection, only once ---
  let jsonLdInjected = false
  watch(
    () => products.value,
    (newList) => {
      if (!jsonLdInjected && newList.length > 0) {
        jsonLdInjected = true
        // products.value is what you want here
        useHead(generateLdJson(products.value))
      }
    }
  )
  
  // --- Actions ---
  function openRightPanel(component: Component) {
    rightPanelComponent.value = markRaw(component)
    isRightPanelOpen.value = true
  }

  function closeRightPanel() {
    isRightPanelOpen.value = false
    rightPanelComponent.value = null
  }

  function toggleRightPanel(component?: Component) {
    if (component) {
      rightPanelComponent.value = markRaw(component)
      isRightPanelOpen.value = true
    } else {
      isRightPanelOpen.value = !isRightPanelOpen.value
      if (!isRightPanelOpen.value) {
        rightPanelComponent.value = null
      }
    }
  }

  return {
    env,
    isFeedbackOpen,
    isAdmin,
    isSafari,
    isMobile,
    showModalDialog,
    user,
    filterParams,
    tags,
    vueFlowHeight,
    carouselHeight,
    showCarousel,
    carouselProducts,
    carouselPercentage,
    lastSelectedFeedbackID,
    selectedCart,
    isRightPanelOpen,
    rightPanelComponent,
    products,
    searchQuery,
    selectedCategory,
    selectedProducts,
    pendingImages,
    loadingImages,
    carts,  // added
    // getters
    categories,
    filteredProducts,

    // actions
    
    openRightPanel,
    closeRightPanel,
    toggleRightPanel,
    imageLoadStart,
    imageLoadEnd,
  }
})
